/**
 * Locale resolution and message lookup.
 *
 * The translator is intentionally synchronous and dependency-free: both
 * catalogues are small enough to ship in the main bundle, and a lazily loaded
 * one would mean the first paint either blocks or flashes English.
 */
import { interpolate } from './format';
import { en } from './locales/en';
import { pt } from './locales/pt';
import type { Locale, LocalePreference, MessageKey, Messages, TranslationValues } from './types';

export const SUPPORTED_LOCALES: readonly Locale[] = ['en', 'pt'];

/** Also the fallback catalogue: every key is guaranteed to exist here. */
export const DEFAULT_LOCALE: Locale = 'en';

/** Endonyms — a language picker should read in its own language. */
export const LOCALE_LABELS: Readonly<Record<Locale, string>> = {
  en: 'English',
  pt: 'Português (Brasil)',
};

/** BCP 47 tags for `<html lang>`; `pt` ships Brazilian copy specifically. */
export const LOCALE_HTML_LANG: Readonly<Record<Locale, string>> = {
  en: 'en',
  pt: 'pt-BR',
};

const CATALOGUES: Readonly<Record<Locale, Messages>> = { en, pt };

export interface Translator {
  readonly locale: Locale;
  t(key: MessageKey, values?: TranslationValues): string;
  has(key: string): boolean;
}

export function isLocale(value: string): value is Locale {
  return (SUPPORTED_LOCALES as readonly string[]).includes(value);
}

export function isLocalePreference(value: string): value is LocalePreference {
  return value === 'system' || isLocale(value);
}

/**
 * Maps one BCP 47 tag onto a shipped locale. Any `pt` region — pt-BR, pt-PT —
 * gets the Brazilian catalogue; that is closer than falling through to English.
 */
function matchLocale(tag: string): Locale | null {
  const primary = tag.toLowerCase().split('-')[0];
  if (primary === 'pt') return 'pt';
  if (primary === 'en') return 'en';
  return null;
}

/** `navigator.languages`, defensively — this also runs in tests and workers. */
function systemLanguages(): readonly string[] {
  if (typeof navigator === 'undefined') return [];
  const languages = navigator.languages;
  if (languages != null && languages.length > 0) return languages;
  return typeof navigator.language === 'string' && navigator.language.length > 0
    ? [navigator.language]
    : [];
}

/**
 * Turns the stored preference into the locale to actually render in. The first
 * browser language we recognise wins, so a `['fr', 'pt-BR']` reader still gets
 * Portuguese rather than English.
 */
export function resolveLocale(
  pref: LocalePreference,
  navigatorLanguages?: readonly string[],
): Locale {
  if (pref !== 'system') return isLocale(pref) ? pref : DEFAULT_LOCALE;

  const tags = navigatorLanguages ?? systemLanguages();
  for (const tag of tags) {
    if (typeof tag !== 'string') continue;
    const matched = matchLocale(tag);
    if (matched !== null) return matched;
  }
  return DEFAULT_LOCALE;
}

/** Own-property lookup: catalogue keys must never resolve to `Object` members. */
function lookup(catalogue: Messages, key: string): string | undefined {
  if (!Object.prototype.hasOwnProperty.call(catalogue, key)) return undefined;
  const value = catalogue[key];
  return typeof value === 'string' ? value : undefined;
}

const cache = new Map<Locale, Translator>();

/**
 * Translators are cached per locale so `useMemo` in the provider hands React a
 * stable value and `t` keeps a stable identity across renders.
 */
export function createTranslator(locale: Locale): Translator {
  const cached = cache.get(locale);
  if (cached) return cached;

  const catalogue = CATALOGUES[locale];

  // Neither member reads `this`, so `const { t } = translator` stays valid.
  const translator: Translator = {
    locale,
    t(key: MessageKey, values?: TranslationValues): string {
      const template = lookup(catalogue, key) ?? lookup(en, key) ?? key;
      return interpolate(template, values);
    },
    has(key: string): boolean {
      return lookup(catalogue, key) !== undefined || lookup(en, key) !== undefined;
    },
  };

  cache.set(locale, translator);
  return translator;
}

/** One-shot translation for code that has a locale but no translator to hand. */
export function translate(locale: Locale, key: MessageKey, values?: TranslationValues): string {
  return createTranslator(locale).t(key, values);
}
